import React from 'react';
import {
    E,
    T,
    useQuotes,
    Order,
    ordersManager
} from '../../logic/imports';
import { OrderDetails } from './OrderDetails';
import { OrderRowDimmer, PriceBar } from '../app/CommonComps';
import * as utils from '../../shared/utils';
import { showDimmer, hideDimmer } from '../app/Dimmer';

interface OrdersDisplayProps {
    orders : Map<string, Order>;
    position: any; // Will be Position class instance
}

const removableStatuses = [
    E.OrderStatus.CANCELLED,
    E.OrderStatus.FILLED,
    E.OrderStatus.REJECTED,
    E.OrderStatus.EXPIRED
]

const editableStatuses = [
    E.OrderStatus.WORKING,
    E.OrderStatus.PARTIALLY_FILLED
]

/**
 * Returns a collection of all orders associated with a position, PositionComp.
 * Groups orders by comboId, so as to visually group them as well.
 */
export const OrdersDisplay: React.FC<OrdersDisplayProps> = (props) => {
    const { position, orders } = props;
    const { quotes } = useQuotes();
    const [orderToEdit, setOrderToEdit] = React.useState<Order | null>(null);
    
    // Track which order IDs are currently being processed (awaiting server confirmation)
    const [processingOrderIds, setProcessingOrderIds] = React.useState<Set<string>>(new Set());
    
    // Force re-render when orders Map changes (detects order mutations like reference type changes)
    const [, setOrdersUpdateTrigger] = React.useState<number>(0);


    const onRemoveClicked = async (order: Order) => {
        try {
            setProcessingOrderIds(prev => new Set([...prev, order.id]));
            await ordersManager.removeOrder(order.id);
        } catch (error: any) {
            console.error('[OrdersDisplay] Order removal failed:', error);
            showDimmer('Failed to remove order: ' + error.message, true);
        } finally {
            // clear processing state
            setProcessingOrderIds(prev => {
                const updated = new Set(prev);
                updated.delete(order.id);
                return updated;
            });
        }
    }

    const onEditOrderClicked = (order: Order) => {
        if (orderToEdit && orderToEdit.id === order.id) {
            setOrderToEdit(null);
        } else {
            setOrderToEdit(order);
        }
    }

    const onCancelOrderClicked = async (order: Order) => {
        try {
            showDimmer('Canceling order...', false);
            await ordersManager.cancelOrder(order.id);
            // close the edit panel if it's open for this order
            if (orderToEdit && orderToEdit.id === order.id) {
                setOrderToEdit(null);
            }
            hideDimmer();
        } catch (error) {
            console.error('[OrderDetails] Order cancellation failed:', error);
            showDimmer('Failed to cancel order.', true);
        }
    }

    // Clean up processingOrderIds when orders are removed
    React.useEffect(() => {
        setProcessingOrderIds(prev => {
            const updated = new Set(prev);
            // Remove any IDs that are no longer in the orders Map
            for (const id of updated) {
                // order was removed, so we can stop tracking it as processing
                if (!orders.has(id)) {
                    updated.delete(id);
                }

                // order was edited and is no longer in the same state,
                // so we can stop tracking it as processing
                const order = orders.get(id);
                if (order && 
                    (editableStatuses.includes(order.status) ||
                    removableStatuses.includes(order.status)) 
                ) {
                    updated.delete(id);
                }
            }
            return updated;
        });
    }, [orders]);

    // Close the edit panel if the edited order transitions to AWAITING_FILL_DATA or FILLED
    React.useEffect(() => {
        if (orderToEdit) {
            const updatedOrder = orders.get(orderToEdit.id);
            if (updatedOrder && (
                updatedOrder.status === E.OrderStatus.AWAITING_FILL_DATA ||
                updatedOrder.status === E.OrderStatus.FILLED
            )) {
                setOrderToEdit(null);
            }
        }
    }, [orders, orderToEdit]);

    const onEditPanelClosed = (hasEdits: boolean, errorMsg?: any) => {
        if (hasEdits && orderToEdit) {
            setProcessingOrderIds(prev => new Set([...prev, orderToEdit.id]));
            setOrderToEdit(null);
        } else if (!hasEdits && errorMsg) {
            showDimmer("Order edit failed: " + errorMsg, true);
        }
    }

    /**
     * Get current price from quotes, preferring contract quote over stock quote
     */
    const getCurrentReferencePrice = (order: Order): number => {
        const orderReference = order.takeProfit?.reference ?? order.stopLoss?.reference ?? E.PriceReference.CONTRACT;
        // Return percent gain directly
        if (orderReference === E.PriceReference.PERCENT) {
            return position.stats?.openPnL ? (position.stats.openPnL / (position.stats.totalCost || 1)) * 100 : 0;
        }

        // Get the appropriate quote based on price reference
        let targetSymbol = order.instrument.symbol; // default to contract symbol
        if (orderReference === E.PriceReference.STOCK && order.instrument.kind === 'option') {
            const underlying = order.instrument.underlying;
            if (underlying) targetSymbol = underlying.symbol;
        }

        const referenceQuote = quotes.find(q => q.instrument.symbol === targetSymbol);
        if (referenceQuote?.last) {
            return referenceQuote.last;
        }
        
        return 0;
    };

    const renderOrderRow = (order: Order) => {
        /** Creates the colored label for order type */
        const orderTypeLabel = (type: string, color: string) => (
            <span className="orderGrid-row-label" style={{ backgroundColor: color }}>
                {type}
            </span>
        );

        const stopLabel = orderTypeLabel('STP', 'red');
        const limitLabel = orderTypeLabel('LMT', 'forestgreen');
        const trailLabel = orderTypeLabel('TRL', 'cyan');

        //#region Helper functions for order display formatting

        /**
         * Get the currency symbol based on price reference type
         * PERCENT shows "%", otherwise "$"
         */
        const getCurrencySymbol = (reference: E.PriceReference): string => {
            return reference === E.PriceReference.PERCENT ? '%' : '$';
        };

        /**
         * Format a price value with appropriate currency symbol
         */
        const formatPrice = (price: number | undefined, reference: E.PriceReference): string => {
            if (price === undefined) return '...';
            const symbol = getCurrencySymbol(reference);
            const formatted = price.toFixed(2);
            return symbol === '$' ? `$${formatted}` : `${formatted}%`;
        };

        //#endregion

        /** Creates the JSX to display order type and price target on the row */
        const getOrderTargets = () => {
            // market orders - no price, no edit button
            if (order.type === E.OrderType.MARKET) {
                let text = 'MKT';
                text += order.side === E.OrderSide.SELL ? ' SELL' : ' BUY';
                return <span>
                    {orderTypeLabel(text, 'gray')}
                </span>;
            }
            // managed (FLEX) order
            if (order.type === E.OrderType.FLEX) {
                const currentPrice = getCurrentReferencePrice(order);
                const orderReference = order.takeProfit?.reference ?? order.stopLoss?.reference ?? E.PriceReference.CONTRACT;
                if (order.trailDistance !== undefined) {
                    return (
                        <PriceBar
                            lowerPrice={order.stopLoss?.value || 0}
                            upperPrice={order.trailDistance || 0}
                            lowerLabel="STOP"
                            upperLabel="TRAIL"
                            currentPrice={currentPrice}
                            reference={orderReference}
                            status={order.status}
                        />
                    );
                }

                return (
                    <PriceBar
                        lowerPrice={order.stopLoss?.value || 0}
                        upperPrice={order.takeProfit?.value || 0}
                        lowerLabel="STOP"
                        upperLabel="LIMIT"
                        currentPrice={currentPrice}
                        reference={orderReference}
                        status={order.status}
                    />
                );
            }

            throw new Error(`Unsupported order type: ${order.type}`);
        };

        let statusColor = '#555';
        switch (order.status) {
            case E.OrderStatus.CANCELLED:
            case E.OrderStatus.REJECTED:
            case E.OrderStatus.PENDING_CANCEL:
            case E.OrderStatus.EXPIRED:
                statusColor = '#ff3232ff';
                break;
            case E.OrderStatus.FILLED:
            case E.OrderStatus.PARTIALLY_FILLED:
                statusColor = '#459221ff';
                break;
            case E.OrderStatus.WORKING:
            case E.OrderStatus.AWAITING_FILL_DATA:
            case E.OrderStatus.PENDING_MODIFICATION:
                statusColor = '#b47500';
                break;
            default:
                statusColor = '#555';
        }

        let gridStyle = 'orderGrid-row';
        if(orderToEdit && orderToEdit.id === order.id)
            gridStyle += ' orderGrid-row--selected';

        return (<div>
            <div className={gridStyle} key={order.id}>

                {/* Type & Price */}
                <div className='orderGrid-item'>
                    {getOrderTargets()}
                </div>

                {/* Fill / Total */}
                <div className='orderGrid-item' style={{
                    border: '1px solid gray',
                }}>
                    {order.stats?.filledQuantity ?? 0} / {order.quantity}
                </div>

                {/* Status */}
                <div className='orderGrid-item' style={{
                    backgroundColor: statusColor,
                    color: '#313131ff'
                }}>
                    <span style={{ whiteSpace: 'pre-wrap' }}>
                        {utils.capitalize(order.status).replace(/_/g, '\n')}
                    </span>
                    {/* add avg fill price if filled */}
                    {order.status === E.OrderStatus.FILLED &&
                        <span style={{ textAlign: 'center' }}> @ {order.stats?.avgFillPrice ?? 'test'/*.toFixed(2)*/}</span>
                    }
                </div>

                {/* Buttons */}
                <div className='orderGrid-item' style={{
                    flexDirection: 'row',
                    gap: '10px',
                    justifyContent: 'flex-end',
                    alignItems: 'center'
                }}>
                    {/* Edit */}
                    {(
                        order.type !== E.OrderType.MARKET &&
                        editableStatuses.includes(order.status) &&
                        !processingOrderIds.has(order.id)
                    ) && (
                        <button
                            className={`iconBtn iconBtn--Edit` + 
                                (orderToEdit && orderToEdit.id === order.id ?
                                ' iconBtn--Edit-active' : '')
                            }
                            onClick={() => onEditOrderClicked(order)}
                        />
                    )}

                    {/* Cancel */}
                    {(
                        order.type !== E.OrderType.MARKET &&
                        editableStatuses.includes(order.status) &&
                        !processingOrderIds.has(order.id)
                    ) && (
                        <button
                            className={`iconBtn iconBtn--Cancel`}
                            onClick={() => onCancelOrderClicked(order)}
                        />
                    )}

                    {/* Remove */}
                    {(
                        removableStatuses.includes(order.status) &&
                        !processingOrderIds.has(order.id)
                    ) && (
                        <button
                            className="iconBtn iconBtn--Hide"
                            onClick={() => onRemoveClicked(order)}
                        />
                    )}
                </div>
                
                {/* Dimmer overlay while processing */}
                <OrderRowDimmer isActive={processingOrderIds.has(order.id)} />
            </div>

            {orderToEdit && orderToEdit.id === order.id && (
            <OrderDetails
                position={position}
                side={orderToEdit.side}
                type={orderToEdit.type}
                onClose={(hasEdits, errorMessage) => onEditPanelClosed(hasEdits, errorMessage)}
                existingOrder={orderToEdit}
            />
            )}
        </div>);
    }

    if (orders.size === 0) {
        return (
            <div className="orderGrid--empty">No orders.</div>
        );
    }

    return (
        <div>
            {/* Orders list display */}
            <div className="orderGrid">
                {Array.from(orders.values()).map(order => (
                    renderOrderRow(order)
                ))}
                {/* Doubles up just for testing */}
                {/* {Array.from(orders.values()).map(order => {
                    let testOrder = {...order};
                    testOrder.id = order.id + '-copy';
                    return renderOrderRow(testOrder);
                })} */}
            </div>
        </div>
    )
}